/**
 * @file Slider 滑杆
 * @description 自定义轨道与拇指；单尺寸；键盘可调。
 *
 * @whenToUse 音量、阈值、数值区间调节。
 * @whenNotToUse 离散枚举档位（用 Radio / Segmented）。
 */

import { useRef, type KeyboardEvent, type PointerEvent } from 'react'
import { cn } from '@/lib/cn.ts'

export type SliderProps = {
  /** 当前值 */
  value: number
  /** 值变化 */
  onChange?: (value: number) => void
  /** 最小值 */
  min?: number
  /** 最大值 */
  max?: number
  /** 步进 */
  step?: number
  /** 禁用 */
  disabled?: boolean
  className?: string
  /** 无障碍标签 */
  'aria-label'?: string
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function snap(value: number, min: number, step: number): number {
  const snapped = Math.round((value - min) / step) * step + min
  return Number(snapped.toFixed(6))
}

export function Slider({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  className,
  'aria-label': ariaLabel = '滑杆',
}: SliderProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const ratio = max === min ? 0 : (clamp(value, min, max) - min) / (max - min)
  const percent = `${ratio * 100}%`

  const setFromClientX = (clientX: number) => {
    const track = trackRef.current
    if (!track || disabled) return
    const rect = track.getBoundingClientRect()
    const raw = ((clientX - rect.left) / rect.width) * (max - min) + min
    onChange?.(clamp(snap(raw, min, step), min, max))
  }

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    event.currentTarget.setPointerCapture(event.pointerId)
    setFromClientX(event.clientX)
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    setFromClientX(event.clientX)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault()
      onChange?.(clamp(value - step, min, max))
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault()
      onChange?.(clamp(value + step, min, max))
    }
    if (event.key === 'Home') {
      event.preventDefault()
      onChange?.(min)
    }
    if (event.key === 'End') {
      event.preventDefault()
      onChange?.(max)
    }
  }

  return (
    <div
      ref={trackRef}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-disabled={disabled || undefined}
      className={cn(
        'relative flex h-8 w-full cursor-pointer items-center transition-opacity duration-200',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onKeyDown={onKeyDown}
    >
      <div className="relative h-1.5 w-full rounded-full bg-border">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width] duration-150"
          style={{ width: percent }}
        />
        <div
          className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary bg-raised transition-[left] duration-150"
          style={{ left: percent }}
        />
      </div>
    </div>
  )
}
